import { useQuery } from "@apollo/client/react";

import DocumentationIconButton from "components/DocumentationIconButton";
import PageWrapper from "components/PageWrapper";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import Card from "ds/components/Card";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";
import NotFoundPage from "components/error/NotFoundPage";
import useErrorHandle from "hooks/useErrorHandle";

import OidcSubjectTemplateForm from "./Form";
import { GET_OIDC_SUBJECT_TEMPLATE } from "./gql";

const DOCS_URL = getDocsUrl("/integrations/cloud-providers/oidc/subject-template");

const OidcSubjectTemplate = () => {
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`Organization Settings · OIDC subject template · ${accountName}`);

  const headerActions = (
    <DocumentationIconButton href={DOCS_URL} tooltipText="Go to OIDC documentation" />
  );

  const { loading, data, error } = useQuery(GET_OIDC_SUBJECT_TEMPLATE);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data) {
    return <PageLoading />;
  }

  if (!data) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Callout variant="warning" title="Changing the template affects cloud trust policies">
        Modifying the subject template changes how OIDC tokens are generated for cloud provider
        integrations (AWS, GCP, Azure). Update your cloud trust policies to accept the new subject
        format before making changes, otherwise runs may fail to authenticate.
      </Callout>
      <PageInfoHeader title="OIDC subject template">{headerActions}</PageInfoHeader>
      <PageWrapper>
        <Box direction="column" limitWidth="medium" gap="x-large" fullWidth>
          <Card>
            <Box direction="column" fullWidth>
              <OidcSubjectTemplateForm currentTemplate={data?.oidcSubjectTemplate} />
            </Box>
          </Card>
        </Box>
      </PageWrapper>
    </>
  );
};

export default OidcSubjectTemplate;
